<template>
  <v-container fluid class="asc-flight-data-template">
    <v-row no-gutters>
      <v-col cols="12" sm="12">
        <template v-if="!isFlightDataPageActive">
          <asc-page-coming-soon
            label="Flight Data Page Coming Soon!"
          ></asc-page-coming-soon>
        </template>
        <template v-if="isFlightDataPageActive">
          <asc-page-header>
            <template #pageTitle>
              <asc-page-title title="FLIGHT DATA"></asc-page-title>
            </template>
          </asc-page-header>

          <asc-notification
            v-if="showAlert"
            :definition="getInvalidPlotAlertDefinition"
            :on-close="handleCloseAlert"
          ></asc-notification>

          <asc-tab id="asc-main-tabs" v-model="model" secondary>
            <asc-tab-title
              title="Plot Editor"
              :disabled="isLookupDataLoading"
            />
            <template v-if="flightDataSavedPlotsPermission">
              <asc-tab-title
                title="Saved Plots"
                :disabled="isLookupDataLoading"
              />
            </template>
          </asc-tab>

          <asc-tab-body :value="model">
            <asc-tab-item class="asc-main-tab-item">
              <asc-create-plot-wrapper
                :page-plots="pagePlots"
                :is-lookup-data-loading="isLookupDataLoading"
                :on-create-page-plot="onCreatePagePlot"
              ></asc-create-plot-wrapper>

              <asc-page-plots-wrapper
                check-global-filter
                :allow-add-to-issue="allowAddToIssue"
                :page-plots="pagePlots"
                :loading-plot-ids="loadingPlotIds"
                :chart-data="chartData"
                :filter-lookups="filterLookups"
                :is-issue-investigator="isIssueInvestigator"
                :is-issue-updater="isIssueUpdater"
                :is-issue-reader="isIssueReader"
                :flight-data-attach-plot-to-mx-rec-permission="
                  flightDataAttachPlotToMxRecPermission
                "
                :plot-editor="plotEditor"
                :associating="associating"
                :saving="saving"
                :disabled="disabled"
                :editable="editable"
                :hide-hidden-tabs="hideHiddenTabs"
                :on-duplicate-page-plot="onDuplicatePagePlot"
                :on-add-issue-to-page-plot="onAddIssueToPagePlot"
                :on-close-page-plot="onClosePagePlot"
                :on-save-page-plot="onSavePagePlot"
                :on-add-tab-to-page-plot="onAddTabToPagePlot"
                :on-remove-tab-from-page-plot="onRemoveTabFromPagePlot"
                :on-update-tab-view-page-plot="onUpdateTabViewPagePlot"
                :on-apply-page-plot="onApplyPagePlot"
                :on-disable-submit="onDisableSubmit"
              ></asc-page-plots-wrapper>
            </asc-tab-item>

            <asc-tab-item class="asc-main-tab-item">
              <template v-if="model === 1">
                <asc-plot-list-wrapper
                  :deleting="deleting"
                  :saved-plots="savedPlots"
                  :on-open-saved-plot="onOpenSavedPlot"
                  :on-delete-saved-plot="onDeleteSavedPlot"
                ></asc-plot-list-wrapper>
              </template>
            </asc-tab-item>
          </asc-tab-body>
        </template>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import AscPageHeader from '@/components/atoms/AscPageHeader'
import AscPageTitle from '@/components/atoms/AscPageTitle'
import AscTab from '@/components/atoms/AscTab'
import AscTabBody from '@/components/atoms/AscTabBody'
import AscTabItem from '@/components/atoms/AscTabItem'
import AscTabTitle from '@/components/atoms/AscTabTitle'
import AscNotification from '@/components/molecules/AscNotification'
import AscPageComingSoon from '@/components/molecules/AscPageComingSoon'
import {
  AscCreatePlotWrapper,
  AscPagePlotsWrapper,
  AscPlotListWrapper,
} from '@/components/organisms'
import AppFeatureFlags from '@/utilities/appFeatureFlags'

export default {
  name: 'AscFlightDataTemplate',

  components: {
    AscPageHeader,
    AscPageTitle,
    AscPageComingSoon,
    AscTab,
    AscTabBody,
    AscTabItem,
    AscTabTitle,
    AscCreatePlotWrapper,
    AscPagePlotsWrapper,
    AscPlotListWrapper,
    AscNotification,
  },

  props: {
    allowAddToIssue: {
      type: Boolean,
      default: false,
    },
    tabIndex: {
      type: Number,
      default: 0,
    },
    pagePlots: {
      type: Array,
      default: () => [],
    },
    loadingPlotIds: {
      type: Array,
      default: () => [],
    },
    chartData: {
      type: Array,
      default: () => [],
    },
    savedPlots: {
      type: Array,
      default: () => [],
    },
    filterLookups: {
      type: Array,
      default: () => [],
    },
    isIssueInvestigator: {
      type: Boolean,
      default: false,
    },
    isIssueUpdater: {
      type: Boolean,
      default: false,
    },
    isIssueReader: {
      type: Boolean,
      default: true,
    },
    plotEditor: {
      type: Boolean,
      default: false,
    },
    associating: {
      type: Boolean,
      default: false,
    },
    saving: {
      type: Boolean,
      default: false,
    },
    deleting: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    editable: {
      type: Boolean,
      default: false,
    },
    hideHiddenTabs: {
      type: Boolean,
      default: false,
    },
    onCreatePagePlot: {
      type: Function,
      required: true,
    },
    onDuplicatePagePlot: {
      type: Function,
      default: () => {},
    },
    onAddIssueToPagePlot: {
      type: Function,
      default: () => {},
    },
    onClosePagePlot: {
      type: Function,
      default: () => {},
    },
    onSavePagePlot: {
      type: Function,
      default: () => {},
    },
    onAddTabToPagePlot: {
      type: Function,
      default: () => {},
    },
    onRemoveTabFromPagePlot: {
      type: Function,
      default: () => {},
    },
    onUpdateTabViewPagePlot: {
      type: Function,
      default: () => {},
    },
    onApplyPagePlot: {
      type: Function,
      default: () => {},
    },
    onOpenSavedPlot: {
      type: Function,
      default: () => {},
    },
    onDeleteSavedPlot: {
      type: Function,
      default: () => {},
    },
    onTabChange: {
      type: Function,
      default: () => {},
    },
    onDisableSubmit: {
      type: Function,
      default: () => {},
    },
    isLookupDataLoading: {
      type: Boolean,
      default: false,
    },
    flightDataAnalyticComparisonPermission: {
      type: Boolean,
      default: false,
    },
    flightDataFlightComparisonPermission: {
      type: Boolean,
      default: false,
    },
    flightDataAnalyticTrendsPermission: {
      type: Boolean,
      default: false,
    },
    flightDataFlightTrendsPermission: {
      type: Boolean,
      default: false,
    },
    flightDataAcmfTrendsPermission: {
      type: Boolean,
      default: false,
    },
    flightDataAttachPlotToMxRecPermission: {
      type: Boolean,
      default: false,
    },
    flightDataSavedPlotsPermission: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      viewModel: this.tabIndex,
      showAlert: false,
      checkGlobalFilter: true,
    }
  },

  computed: {
    model: {
      get() {
        return this.viewModel
      },
      set(newVal) {
        this.viewModel = newVal
        if (this.onTabChange) {
          this.onTabChange(newVal)
        }
      },
    },
    getInvalidPlotAlertDefinition() {
      return [
        {
          color: 'warning',
          isActive: true,
          text: 'Filtered plots are not displayed. Please deactivate Global Filter to view all available plots.',
          type: 'warning',
          dismissible: true,
        },
      ]
    },

    isFlightDataPageActive() {
      return AppFeatureFlags.getFlightDataPage()
    },
    invalidPagePlots() {
      return this.pagePlots.length > 0
        ? this.pagePlots.filter(item => !item.globalFilterValid)
        : []
    },
  },

  watch: {
    tabIndex(newValue) {
      this.viewModel = newValue
    },
    pagePlots: {
      deep: true,
      handler() {
        this.showAlert = this.invalidPagePlots.length > 0
      },
    },
  },

  methods: {
    handleCloseAlert() {
      this.showAlert = false
    },
  },
}
</script>

<style lang="scss">
.asc-flight-data-template {
  background-color: $gray-100;

  .asc-notification-wrapper {
    .asc-notification {
      margin-bottom: 16px;
    }
  }

  #asc-main-tabs.asc-tabs {
    .v-tabs-bar__content {
      background-color: $primary-white !important;
    }
  }

  .asc-tab__content--primary {
    background-color: $primary-white !important;

    .asc-main-tab-item {
      .asc-tab__content--secondary {
        .asc-tab__content--primary {
          background-color: $gray-0 !important;
        }
      }
    }
  }

  .asc-container--container .asc-container--headerWrapper {
    padding: 0 16px 8px 16px;
  }
}
</style>
